/**
 * Record — the four-step import surface (PRD-session-results-import §6):
 * upload → map columns → resolve items → confirm. The done state names the
 * three exits: open the session, review sweep findings, import another file.
 *
 * The wizard never writes until Confirm; blockers surface on the resolve
 * step before anything exists. Publishing stays a human action in the
 * Session app afterward — import lands at RUN_COMPLETE, never PUBLISHED.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Download, FileSpreadsheet, GitMerge, Link2, Plus, Upload } from 'lucide-react';
import { useUserStore } from '@/stores/prioritization/userStore';
import { SESSION_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import { importFromExcel, suggestColumnMappings, type ExcelImportResult } from '@/lib/excelService';
import { ColumnMapper, type ColumnMappingState } from '@/components/prioritization/import-export/ColumnMapper';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton, StatusChip } from '../components/primitives';
import { StepRail, type FlowStep } from '../components/flow';
import {
  buildFullResultsTemplateCsv,
  buildRankedListTemplateCsv,
  parseSessionImport,
  SESSION_IMPORT_FIELDS,
  type ParsedSessionImport,
  type ParticipantMatch,
} from './sessionImportParser';
import {
  commitSessionImport,
  matchImportParticipants,
  resolveImportItems,
  runImportSweep,
  type ItemResolution,
  type SweepOutcome,
} from './sessionImportActions';

type Step = 'upload' | 'map' | 'resolve' | 'confirm';

interface LoadedFile {
  name: string;
  sheets: ExcelImportResult['sheets'];
  sheetIndex: number;
}

type SweepState =
  | { status: 'idle' }
  | { status: 'running'; count: number }
  | { status: 'done'; outcome: SweepOutcome };

const RANKING_SOURCE_COPY: Record<ParsedSessionImport['rankingSource'], string> = {
  'explicit-rank': 'Ranking taken from the file’s rank column.',
  score: 'Ranking derived from the score column, highest first.',
  'computed-scores': 'Ranking computed from the scores: weighted mean of each criterion’s average.',
  'file-order': 'No rank or score mapped — the file’s row order is the ranking.',
};

/** ColumnMapper auto-suggests internally but only reports user edits, so the
 *  screen seeds the same suggestions as the initial shared mapping state. */
function initialMappingsFor(headers: string[]): ColumnMappingState {
  const suggestions = suggestColumnMappings(
    headers,
    SESSION_IMPORT_FIELDS.map((f) => ({ key: f.key, label: f.label }))
  );
  return Object.fromEntries(headers.map((h) => [h, suggestions[h] ?? null]));
}

/** Who in the file is a member — matched by exact email only, never by name. */
function participantMatchLine(matches: ParticipantMatch[]): string {
  const matched = matches.filter((m) => m.userId).length;
  const byName = matches.length - matched;
  if (matched === 0) {
    return 'Participants are recorded by name — none has the email of a workspace member. No accounts are created.';
  }
  const seated = `${matched} participant${matched === 1 ? '' : 's'} matched to workspace members by email and added to the session`;
  return byName > 0
    ? `${seated}; ${byName} recorded by name. No accounts are created.`
    : `${seated}. No accounts are created.`;
}

function downloadCsv(fileName: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SessionImportScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('session-import')!;
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  // Importing creates a session (prioritization_sessions insert, migration 754:
  // SESSION_RUN) and an item for every row not linked to an existing one
  // (items insert, migration 751: items.create). The screen is drawn only for
  // a session creator; moving on with rows still marked "create" only for an
  // item creator too (build contract §2.8).
  const createRights = useCreateRights();

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<LoadedFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<ColumnMappingState>({});
  const [parsed, setParsed] = useState<ParsedSessionImport | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolutions, setResolutions] = useState<ItemResolution[]>([]);
  const [participantMatches, setParticipantMatches] = useState<ParticipantMatch[]>([]);
  const [sessionName, setSessionName] = useState('');
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [doneSessionId, setDoneSessionId] = useState<string | null>(null);
  const [doneSummary, setDoneSummary] = useState<{ created: number; linked: number } | null>(null);
  const [sweep, setSweep] = useState<SweepState>({ status: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sheet = file ? file.sheets[file.sheetIndex] : null;
  const newItemCount = resolutions.filter((r) => r.action === 'create').length;
  const itemsBlocked = newItemCount > 0 && !createRights.items;
  const itemsBlockedLine = `${newItemCount} ${newItemCount === 1 ? 'row is' : 'rows are'} set to create a new item, which takes the Create Items permission. Link ${newItemCount === 1 ? 'it' : 'each one'} to an existing item instead, or ask a workspace admin for the permission.`;
  const titleMapped = useMemo(
    () => Object.values(mappings).includes('title'),
    [mappings]
  );

  const handleFile = useCallback(async (picked: File) => {
    setFileError(null);
    const lower = picked.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      setFileError('That file type isn’t supported — drop a .csv, .xlsx, or .xls file.');
      return;
    }
    const result = await importFromExcel(picked);
    const sheets = result.success ? result.sheets.filter((s) => s.data.length > 0) : [];
    if (!result.success || sheets.length === 0) {
      setFileError(result.error ?? 'No rows found in that file.');
      return;
    }
    setFile({ name: picked.name, sheets, sheetIndex: 0 });
    setMappings(initialMappingsFor(sheets[0].headers));
    setParsed(null);
    setResolutions([]);
    setSessionName(picked.name.replace(/\.(csv|xlsx|xls)$/i, ''));
    setStep('map');
  }, []);

  const goResolve = useCallback(async () => {
    if (!sheet || resolving) return;
    const mapped = sheet.data.map((row) => {
      const out: Record<string, unknown> = {};
      for (const [column, field] of Object.entries(mappings)) {
        if (field) out[field] = row[column];
      }
      return out;
    });
    const nextParsed = parseSessionImport(mapped);
    setResolving(true);
    const [nextResolutions, nextMatches] = await Promise.all([
      resolveImportItems(nextParsed.items),
      matchImportParticipants(nextParsed),
    ]);
    setResolving(false);
    setParsed(nextParsed);
    setResolutions(nextResolutions);
    setParticipantMatches(nextMatches);
    setStep('resolve');
  }, [sheet, mappings, resolving]);

  const toggleResolution = useCallback((key: string) => {
    setResolutions((prev) =>
      prev.map((r) =>
        r.key === key && r.matchItemId
          ? { ...r, action: r.action === 'link' ? 'create' : 'link' }
          : r
      )
    );
  }, []);

  const handleCommit = useCallback(async () => {
    if (!parsed || !file || !userId) return;
    setCommitting(true);
    setCommitError(null);
    const result = await commitSessionImport({
      sessionName: sessionName.trim() || file.name,
      fileName: file.name,
      parsed,
      resolutions,
      participantMatches,
      userId,
      userName: userName || 'Facilitator',
    });
    setCommitting(false);
    if (!result.success || !result.sessionId) {
      setCommitError(result.error ?? 'The import failed.');
      return;
    }
    setDoneSessionId(result.sessionId);
    setDoneSummary({ created: result.createdItemIds.length, linked: result.linkedCount });
    if (result.createdItemIds.length > 0) {
      setSweep({ status: 'running', count: result.createdItemIds.length });
      void runImportSweep(result.createdItemIds).then((outcome) =>
        setSweep({ status: 'done', outcome })
      );
    } else {
      setSweep({ status: 'done', outcome: { ran: false, reason: 'no-new-items' } });
    }
  }, [parsed, file, userId, userName, sessionName, resolutions, participantMatches]);

  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setFileError(null);
    setMappings({});
    setParsed(null);
    setResolutions([]);
    setParticipantMatches([]);
    setSessionName('');
    setCommitError(null);
    setDoneSessionId(null);
    setDoneSummary(null);
    setSweep({ status: 'idle' });
  }, []);

  // ── Done state ─────────────────────────────────────────────────────────────
  if (doneSessionId && parsed) {
    const createdCount = doneSummary?.created ?? 0;
    return (
      <AppShell app={app}>
        <ScreenHeading
          title="It’s on the record"
          subtitle="The imported results are now a real session — same record, same rules as one run here."
        />
        <BallLine ballWith="you — review, then publish when ready" />
        <AppCard>
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-alpine-green-100 text-alpine-green-700 dark:bg-alpine-green-900/40 dark:text-alpine-green-300">
              <Check className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                {sessionName || file?.name}
              </p>
              <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                {parsed.items.length} ranked item{parsed.items.length === 1 ? '' : 's'}
                {doneSummary ? ` — ${doneSummary.linked} linked, ${createdCount} new` : ''}
                {parsed.participants.length > 0
                  ? `, scores from ${parsed.participants.length} participant${parsed.participants.length === 1 ? '' : 's'}`
                  : ''}
                {parsed.criteria.length > 0
                  ? ` across ${parsed.criteria.length} criteri${parsed.criteria.length === 1 ? 'on' : 'a'}`
                  : ''}
                .
              </p>
              <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                Publishing is a separate, deliberate step — you’ll find it in the session when you’re ready.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <PrimaryButton onClick={() => navigate(`/apps/session/${doneSessionId}/host`)}>
              Open the session <ArrowRight className="h-4 w-4" />
            </PrimaryButton>
            <SecondaryButton onClick={reset}>Import another file</SecondaryButton>
          </div>
        </AppCard>

        {/* The reconciler comes back around */}
        <div className="mt-4">
          <AppCard>
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-[var(--surface-raised)]">
                <GitMerge className="h-4 w-4 text-gray-600 dark:text-[var(--text-sec)]" />
              </span>
              <div className="min-w-0 flex-1">
                <Eyebrow>Duplicate sweep</Eyebrow>
                {sweep.status === 'running' ? (
                  <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    Checking {sweep.count} new item{sweep.count === 1 ? '' : 's'} against your pool…
                  </p>
                ) : sweep.status === 'done' && sweep.outcome.ran ? (
                  sweep.outcome.proposed > 0 ? (
                    <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                      {sweep.outcome.proposed} possible duplicate{' '}
                      {sweep.outcome.proposed === 1 ? 'cluster' : 'clusters'} flagged —{' '}
                      <Link to="/prioritization/agents/twins" className="text-primary-600 underline-offset-2 hover:underline dark:text-primary-300">
                        review and merge
                      </Link>
                      . Nothing merges without you.
                    </p>
                  ) : (
                    <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                      No duplicates flagged — the new items look distinct from your pool.
                    </p>
                  )
                ) : sweep.status === 'done' && !sweep.outcome.ran && sweep.outcome.reason === 'agent-off' ? (
                  <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    The Twins agent is off, so the new items weren’t swept for duplicates. Enable it under
                    Settings → Agents to have imports checked automatically.
                  </p>
                ) : sweep.status === 'done' && !sweep.outcome.ran && sweep.outcome.reason === 'error' ? (
                  <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    The duplicate sweep couldn’t run — you can run Twins any time from its report page.
                  </p>
                ) : (
                  <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    Every item was linked to one that already exists — nothing new to sweep.
                  </p>
                )}
              </div>
            </div>
          </AppCard>
        </div>
      </AppShell>
    );
  }

  // ── The wizard ─────────────────────────────────────────────────────────────
  const steps: FlowStep[] = [
    {
      key: 'upload',
      label: 'Upload the results',
      detail: file ? file.name : 'A ranked list, or a full scoring workbook',
      status: step === 'upload' ? 'current' : 'done',
    },
    {
      key: 'map',
      label: 'Map the columns',
      detail: 'Tell us what each column is',
      status: step === 'map' ? 'current' : step === 'upload' ? 'todo' : 'done',
    },
    {
      key: 'resolve',
      label: 'Resolve the items',
      detail: 'Link to existing items, or create new ones',
      status:
        step === 'resolve'
          ? parsed && parsed.blockers.length > 0
            ? 'warn'
            : 'current'
          : step === 'confirm'
            ? 'done'
            : 'todo',
    },
    {
      key: 'confirm',
      label: 'Confirm and import',
      detail: 'Nothing is written until here',
      status: step === 'confirm' ? 'current' : 'todo',
    },
  ];

  const stepIndex: Record<Step, number> = { upload: 0, map: 1, resolve: 2, confirm: 3 };
  const handleStepSelect = (key: string) => {
    // Back-navigation only — forward movement goes through each step's CTA.
    if (stepIndex[key as Step] < stepIndex[step]) setStep(key as Step);
  };

  if (!createRights.session) {
    return (
      <AppShell app={app} wide>
        <ScreenHeading title="Put results on the record" />
        <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="session-import-refused">
          {SESSION_CREATE_LINE}
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell app={app} wide>
      <ScreenHeading
        title="Put results on the record"
        subtitle="You ran the prioritization somewhere else. This turns the file into a real session — items, ranking, scores and all."
      />
      <BallLine ballWith="you — the import" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px,1fr]">
        <div>
          <StepRail steps={steps} onSelect={handleStepSelect} />
        </div>

        <div className="min-w-0 space-y-4">
          {step === 'upload' ? (
            <>
              <AppCard>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const dropped = e.dataTransfer.files?.[0];
                    if (dropped) void handleFile(dropped);
                  }}
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10 text-center dark:border-[var(--rule)]"
                >
                  <Upload className="h-6 w-6 text-gray-400" />
                  <p className="mt-3 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    Drop a .csv or Excel file here
                  </p>
                  <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    A simple ranked list works. So does a full workbook with participants, criteria, and scores.
                  </p>
                  <SecondaryButton className="mt-4" onClick={() => fileInputRef.current?.click()}>
                    <FileSpreadsheet className="h-4 w-4" /> Choose a file
                  </SecondaryButton>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      const picked = e.target.files?.[0];
                      if (picked) void handleFile(picked);
                      e.target.value = '';
                    }}
                  />
                </div>
                {fileError ? (
                  <p className="mt-3 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
                    {fileError}
                  </p>
                ) : null}
              </AppCard>
              <AppCard>
                <Eyebrow>Starting from scratch?</Eyebrow>
                <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                  Download a template, fill it in (or hand it to whoever has the results), and drop it back here.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <SecondaryButton
                    onClick={() => downloadCsv('session-results-ranked-list.csv', buildRankedListTemplateCsv())}
                  >
                    <Download className="h-4 w-4" /> Ranked list template
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => downloadCsv('session-results-full.csv', buildFullResultsTemplateCsv())}
                  >
                    <Download className="h-4 w-4" /> Full results template
                  </SecondaryButton>
                </div>
              </AppCard>
            </>
          ) : null}

          {step === 'map' && sheet ? (
            <>
              {file && file.sheets.length > 1 ? (
                <AppCard>
                  <Eyebrow>Sheet</Eyebrow>
                  <select
                    value={file.sheetIndex}
                    onChange={(e) => {
                      const nextIndex = Number(e.target.value);
                      setFile({ ...file, sheetIndex: nextIndex });
                      setMappings(initialMappingsFor(file.sheets[nextIndex].headers));
                    }}
                    className="mt-2 rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
                  >
                    {file.sheets.map((s, i) => (
                      <option key={s.name} value={i}>
                        {s.name} ({s.data.length} rows)
                      </option>
                    ))}
                  </select>
                </AppCard>
              ) : null}
              <AppCard>
                <Eyebrow>Map the columns</Eyebrow>
                <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                  Only <span className="font-medium">Item title</span> is required. Map{' '}
                  <span className="font-medium">participant</span>, <span className="font-medium">criterion</span>, and{' '}
                  <span className="font-medium">score</span> if the file has one row per score. Map{' '}
                  <span className="font-medium">participant email</span> to link people to the members with that
                  exact email.
                </p>
                <div className="mt-4">
                  <ColumnMapper
                    key={`${file?.name}-${file?.sheetIndex}`}
                    excelHeaders={sheet.headers}
                    appFields={SESSION_IMPORT_FIELDS}
                    initialMappings={mappings}
                    onChange={setMappings}
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <PrimaryButton onClick={() => void goResolve()} disabled={!titleMapped || resolving}>
                    {resolving ? 'Checking your items…' : 'Continue'} <ArrowRight className="h-4 w-4" />
                  </PrimaryButton>
                </div>
              </AppCard>
            </>
          ) : null}

          {step === 'resolve' && parsed ? (
            <>
              {parsed.blockers.length > 0 ? (
                <AppCard className="border-luminous-coral-300 dark:border-luminous-coral-800">
                  <Eyebrow>Fix these first</Eyebrow>
                  <ul className="mt-2 list-disc space-y-1 pl-5 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
                    {parsed.blockers.map((blocker) => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    <SecondaryButton onClick={() => setStep('map')}>Back to mapping</SecondaryButton>
                  </div>
                </AppCard>
              ) : (
                <AppCard>
                  <Eyebrow>
                    {parsed.items.length} item{parsed.items.length === 1 ? '' : 's'} in the file
                  </Eyebrow>
                  <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    Each one either links to an item you already have or is created new. Flip any row we
                    got wrong — the choice is yours before anything is written.
                  </p>
                  <div className="mt-4 space-y-2">
                    {resolutions.map((resolution) => (
                      <div
                        key={resolution.key}
                        className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 dark:border-[var(--rule)]"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                            {resolution.title}
                          </p>
                          {resolution.action === 'link' && resolution.matchTitle ? (
                            <p className="truncate font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                              Matches “{resolution.matchTitle}” by{' '}
                              {resolution.matchReason === 'external-id'
                                ? 'external ID'
                                : resolution.matchReason === 'id'
                                  ? 'platform ID'
                                  : 'title'}
                            </p>
                          ) : resolution.note ? (
                            <p className="truncate font-body text-xs text-electric-mustard-800 dark:text-electric-mustard-300">
                              {resolution.note}
                            </p>
                          ) : null}
                        </div>
                        {resolution.action === 'link' ? (
                          <StatusChip label="Link existing" tone="green" />
                        ) : (
                          <StatusChip label="Create new" tone="slate" />
                        )}
                        {resolution.matchItemId ? (
                          <SecondaryButton
                            className="px-2.5 py-1.5 text-xs"
                            onClick={() => toggleResolution(resolution.key)}
                          >
                            {resolution.action === 'link' ? (
                              <>
                                <Plus className="h-3.5 w-3.5" /> Create instead
                              </>
                            ) : (
                              <>
                                <Link2 className="h-3.5 w-3.5" /> Link instead
                              </>
                            )}
                          </SecondaryButton>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end">
                    {itemsBlocked ? (
                      <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="session-import-items-refused">
                        {itemsBlockedLine}
                      </p>
                    ) : (
                      <PrimaryButton onClick={() => setStep('confirm')}>
                        Continue <ArrowRight className="h-4 w-4" />
                      </PrimaryButton>
                    )}
                  </div>
                </AppCard>
              )}
            </>
          ) : null}

          {step === 'confirm' && parsed ? (
            <AppCard>
              <Eyebrow>Confirm and import</Eyebrow>
              <label
                className="mt-3 block font-body text-sm text-gray-700 dark:text-[var(--text-sec)]"
                htmlFor="import-session-name"
              >
                Session name
              </label>
              <input
                id="import-session-name"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
              <p className="mt-4 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                This creates a completed session with{' '}
                <span className="font-medium">{parsed.items.length} ranked items</span> —{' '}
                {resolutions.filter((r) => r.action === 'link').length} linked to existing items,{' '}
                {resolutions.filter((r) => r.action === 'create').length} created new
                {parsed.participants.length > 0 ? (
                  <>
                    {' '}
                    — with scores from{' '}
                    <span className="font-medium">
                      {parsed.participants.length} participant{parsed.participants.length === 1 ? '' : 's'}
                    </span>{' '}
                    across{' '}
                    <span className="font-medium">
                      {parsed.criteria.length} criteri{parsed.criteria.length === 1 ? 'on' : 'a'}
                    </span>
                  </>
                ) : null}
                .
              </p>
              <ul className="mt-3 space-y-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                <li>{RANKING_SOURCE_COPY[parsed.rankingSource]}</li>
                {parsed.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
                {parsed.participants.length > 0 ? <li>{participantMatchLine(participantMatches)}</li> : null}
              </ul>
              {commitError ? (
                <p className="mt-3 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
                  {commitError}
                </p>
              ) : null}
              <div className="mt-4 flex justify-end gap-2">
                <SecondaryButton onClick={() => setStep('resolve')} disabled={committing}>
                  Back
                </SecondaryButton>
                {itemsBlocked ? (
                  <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">{itemsBlockedLine}</p>
                ) : (
                  <PrimaryButton onClick={() => void handleCommit()} disabled={committing || !sessionName.trim()}>
                    {committing ? 'Importing…' : 'Import to the record'}
                  </PrimaryButton>
                )}
              </div>
            </AppCard>
          ) : null}
        </div>
      </div>
    </AppShell>
  );
}
